import { useState, useEffect } from 'react';
import { useDataApi } from '@adsk/alloy-helpers';
import { useLocale } from '@adsk/alloy-react-locale';
import { useCurrentProjectId } from '@adsk/alloy-provider';
import { useFormikContext } from 'formik';
import { PERMISSION_TYPES } from '../constants';
import {
  buildGetIssuesPermissionsRequest,
  getIssuesPermissionsInfo,
} from './utils';

/**
 * Hook to get issues permissions
 * @param {object} props - The props object
 * @param {boolean} props.shouldUseNewIssuePermissions - Flag to use new issue permissions
 */
const useIssuesPermissions = ({ shouldUseNewIssuePermissions }) => {
  const [permissionsInfo, setPermissionsInfo] = useState({ loading: true });
  const { values } = useFormikContext();
  const {
    member: {
      autodeskId: memberAutodeskId,
      selectedCompany,
      selectedRoles,
      selectedAccessLevel,
    },
  } = values;
  const projectId = useCurrentProjectId();
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
  const {
    data: issuesPermissionsData,
    doFetch: doFetchIssuesPermissions,
    error: issuesPermissionsError,
  } = useDataApi();

  useEffect(() => {
    if (projectId === undefined) return;

    doFetchIssuesPermissions(buildGetIssuesPermissionsRequest(projectId));
  }, [projectId, doFetchIssuesPermissions]);

  useEffect(() => {
    if (!issuesPermissionsData) return;

    const memberPermissions = memberAutodeskId
      ? issuesPermissionsData.results.find(
          (permission) =>
            permission.subject === memberAutodeskId &&
            permission.subjectType === PERMISSION_TYPES.USER
        )
      : null;
    const companyPermissions = selectedCompany
      ? issuesPermissionsData.results.find(
          (permission) =>
            permission.subject === selectedCompany.memberGroupId &&
            permission.subjectType === PERMISSION_TYPES.COMPANY
        )
      : null;
    const rolesPermissions = selectedRoles
      ? issuesPermissionsData.results.filter(
          (permission) =>
            selectedRoles.some(
              (role) => role.memberGroupId === permission.subject
            ) && permission.subjectType === PERMISSION_TYPES.ROLE
        )
      : [];

    const info = getIssuesPermissionsInfo(
      memberPermissions,
      companyPermissions,
      rolesPermissions,
      selectedAccessLevel?.value,
      selectedCompany,
      selectedRoles,
      T,
      shouldUseNewIssuePermissions
    );

    setPermissionsInfo(info);
  }, [
    issuesPermissionsData,
    memberAutodeskId,
    selectedAccessLevel,
    selectedCompany,
    selectedRoles,
    T,
    shouldUseNewIssuePermissions,
  ]);

  useEffect(() => {
    if (!issuesPermissionsError) return;

    setPermissionsInfo({});
  }, [issuesPermissionsError]);

  return permissionsInfo;
};
export default useIssuesPermissions;
